import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  DialogActions,
  Divider,
  useTheme,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { City, Country } from "../../../../../../types/site-params/Country";
import { FilterType } from "../../../../../../types/Filter";
import {
  clientOptions,
  cultureOptions,
  lastSignInOptions,
  ourContactOptions,
  postedVacsOptions,
  serviceRequiredOptions,
  yesNoOptions,
} from "./mockData";
import { useCountriesForAutocomplete } from "../../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../../hooks/query-hooks/useCities";
import { useDebounce } from "../../../../../../hooks";

interface FiltersModalProps {
  open: boolean;
  handleClose: () => void;
  onApplyFilters: (filters: FilterType) => void;
  onClearFilters: () => void;
}

const FiltersModal = ({
  open,
  handleClose,
  onApplyFilters,
}: FiltersModalProps) => {
  const theme = useTheme();

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(
    selectedCountry?.id || null,
    20,
    debouncedCitySearchTerm
  );

  const [contacted, setContacted] = useState<string>("");
  const [client, setClient] = useState<string>("");
  const [satisfied, setSatisfied] = useState<string>("");
  const [financialAbility, setFinancialAbility] = useState<string>("");
  const [hrDept, setHrDept] = useState<string>("");
  const [trusted, setTrusted] = useState<string>("");
  const [serviceRequired, setServiceRequired] = useState<string>("");
  const [ourContact, setOurContact] = useState<string>("");
  const [culture, setCulture] = useState<string>("");
  const [lastSignIn, setLastSignIn] = useState<string>("");
  const [postedVacs, setPostedVacs] = useState<string>("");

  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  const handleApply = () => {
    onApplyFilters({
      contacted,
      client,
      satisfied,
      trusted,
      culture,
      financial_ability: financialAbility,
      hr_dept: hrDept,
      service_required: serviceRequired,
      our_contact: ourContact,
      last_sign_in: lastSignIn,
      posted_vacs: postedVacs,
      country: selectedCountry ? selectedCountry.id.toString() : "",
      city: selectedCity ? selectedCity.id.toString() : "",
    });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: 500 }}>
        <FormattedMessage id="Filters" />
      </DialogTitle>
      <Divider sx={{ marginInline: "1rem" }} />
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedCountry}
                onChange={(event, newValue) => {
                  setSelectedCountry(newValue);
                  setSelectedCity(null);
                  // Clear search term when option is selected
                  if (newValue) {
                    setCountrySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCountrySearchTerm(newInputValue);
                  }
                }}
                options={countries}
                getOptionLabel={(option: Country) =>
                  theme.direction === "rtl" ? option.name_trans.ar : option.name
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Country" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCountries && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreCountries &&
                      !loadingCountries
                    ) {
                      loadMoreCountries();
                    }
                  },
                }}
                loading={loadingCountries}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedCountrySearchTerm.length >= 2
                    ? "No countries found"
                    : "Type to search countries"
                }
              />
              {countriesError && (
                <FormHelperText error>
                  Error loading countries. Please try again.
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedCity}
                onChange={(event, newValue) => {
                  setSelectedCity(newValue);
                  // Clear search term when option is selected
                  if (newValue) {
                    setCitySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCitySearchTerm(newInputValue);
                  }
                }}
                options={cities}
                getOptionLabel={(option: City) =>
                  theme.direction === "rtl" ? option.name_trans.ar : option.name
                }
                disabled={!selectedCountry}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="City" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCities && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreCities &&
                      !loadingCities
                    ) {
                      loadMoreCities();
                    }
                  },
                }}
                loading={loadingCities}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedCitySearchTerm.length >= 2
                    ? "No cities found"
                    : "Type to search cities"
                }
              />
              {citiesError && (
                <FormHelperText error>
                  Error loading cities. Please try again.
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Contacted" />
                </InputLabel>
                <Select
                  value={contacted}
                  onChange={(e) => setContacted(e.target.value)}
                  label={<FormattedMessage id="Contacted" />}
                >
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Client" />
                </InputLabel>
                <Select
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  label={<FormattedMessage id="Client" />}
                >
                  {clientOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Satisfied" />
                </InputLabel>
                <Select
                  value={satisfied}
                  onChange={(e) => setSatisfied(e.target.value)}
                  label={<FormattedMessage id="Satisfied" />}
                >
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Financial Ability" />
                </InputLabel>
                <Select
                  value={financialAbility}
                  onChange={(e) => setFinancialAbility(e.target.value)}
                  label={<FormattedMessage id="Financial Ability" />}
                >
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="HR Department" />
                </InputLabel>
                <Select
                  value={hrDept}
                  onChange={(e) => setHrDept(e.target.value)}
                  label={<FormattedMessage id="HR Department" />}
                >
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Trusted" />
                </InputLabel>
                <Select
                  value={trusted}
                  onChange={(e) => setTrusted(e.target.value)}
                  label={<FormattedMessage id="Trusted" />}
                >
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Service Required" />
                </InputLabel>
                <Select
                  value={serviceRequired}
                  onChange={(e) => setServiceRequired(e.target.value)}
                  label={<FormattedMessage id="Service Required" />}
                >
                  {serviceRequiredOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Our Contact" />
                </InputLabel>
                <Select
                  value={ourContact}
                  onChange={(e) => setOurContact(e.target.value)}
                  label={<FormattedMessage id="Our Contact" />}
                >
                  {ourContactOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Culture" />
                </InputLabel>
                <Select
                  value={culture}
                  onChange={(e) => setCulture(e.target.value)}
                  label={<FormattedMessage id="Culture" />}
                >
                  {cultureOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}{" "}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Last Sign In" />
                </InputLabel>
                <Select
                  value={lastSignIn}
                  onChange={(e) => setLastSignIn(e.target.value)}
                  label={<FormattedMessage id="Last Sign In" />}
                >
                  {lastSignInOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <FormattedMessage id="Posted Vacancies" />
                </InputLabel>
                <Select
                  value={postedVacs}
                  onChange={(e) => setPostedVacs(e.target.value)}
                  label={<FormattedMessage id="Posted Vacancies" />}
                >
                  {postedVacsOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {theme.direction === "rtl"
                        ? option.arabic_label
                        : option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "1rem" }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Cancel" />
        </Button>
        {/* <Button variant="outlined" onClick={onClearFilters} color="primary">
          <FormattedMessage id="Clear Filters" />
        </Button> */}

        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Apply" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModal;
